import { createReducer, Reducer } from "@reduxjs/toolkit";
import { Pageable } from "../../services/pageable";
import { RemoteResource } from "../common/fetchable";
import { EmptyFetchable, Fetchable } from "../common/fetchable/helpers";
import { VaultActions } from "./actions";
import { VaultsDTO } from "./models/dto";

export namespace Vaults {
  export const vaults: RemoteResource<
    VaultsDTO.Responses.GetAllVaults.T,
    VaultsDTO.Responses.GetAllVaults.T,
    Pageable.T<keyof VaultsDTO.Responses.GetSingleVault.T>
  > = {
    fetch: VaultActions.setAllVaultsPagination,
    updateStore: VaultActions.setAllVaults,
    selector: (store) => store.vaults.all.data,
  };
}

/**
 * Interface used for Redux Store
 */
export interface Vaults {
  all: {
    data: Fetchable<VaultsDTO.Responses.GetAllVaults.T>;
    pagination: Pageable.T<keyof VaultsDTO.Responses.GetSingleVault.T>;
  }; //TODO move pagination and vaults to their own object
  singleVaultMap: Record<
    string,
    | (VaultsDTO.Responses.GetSingleVault.T & {
        entries: {
          data: Fetchable<VaultsDTO.Responses.GetAllEntries.T>;
          pagination: Pageable.T<keyof VaultsDTO.Responses.GetSingleEntry.T>;
        };
      })
    | "LOADING"
  >;
}

const defaultEntries = {
  data: EmptyFetchable,
  pagination: {},
};

const initialState: Vaults = {
  all: {
    data: EmptyFetchable,
    pagination: {},
  },
  singleVaultMap: {},
};

// Reducer
export const vaultsReducer: Reducer<
  Vaults,
  VaultActions.Actions
> = createReducer(initialState, (builder) => {
  builder
    .addCase(VaultActions.getAllVaults, (state) => {
      state.all.data = {
        status: "loading",
      };
    })
    .addCase(VaultActions.setAllVaultsPagination, (state, action) => {
      state.all.pagination = action.payload;
      state.all.data = {
        status: "loading",
      };
    })
    .addCase(VaultActions.setAllVaults, (state, action) => {
      state.all.data = action.payload;
      if (action.payload.status === "ready") {
        action.payload.data.content.forEach((p) => {
          state.singleVaultMap[p.id] = {
            ...p,
            entries: defaultEntries,
          };
        });
      }
    })
    .addCase(VaultActions.resetAllVaults, (state) => {
      state.all.data = EmptyFetchable;
      state.all.pagination = {};
    })
    .addCase(VaultActions.setSingleVaults, (state, action) => {
      state.singleVaultMap[action.payload.id] = {
        ...action.payload.result,
        entries: defaultEntries,
      };
    })

    .addCase(VaultActions.getAllVaultEntries, (state, action) => {
      const selectedVault = state.singleVaultMap[action.payload];

      if (selectedVault !== "LOADING") {
        selectedVault.entries.data = {
          status: "loading",
        };
      }
    })
    .addCase(VaultActions.setAllVaultEntriesPagination, (state, action) => {
      const selectedVault = state.singleVaultMap[action.payload.vaultId];
      if (selectedVault !== "LOADING") {
        selectedVault.entries.pagination = action.payload.pagination;
        selectedVault.entries.data = {
          status: "loading",
        };
      }
    })
    .addCase(VaultActions.setAllVaultEntries, (state, action) => {
      const selectedVault = state.singleVaultMap[action.payload.vaultId];
      if (selectedVault !== "LOADING") {
        selectedVault.entries.data = action.payload.entries;
      }
    })
    .addCase(VaultActions.resetAllVaultEntries, (state, action) => {
      const selectedVault = state.singleVaultMap[action.payload];
      if (selectedVault !== "LOADING") {
        selectedVault.entries = defaultEntries;
      }
    });
});
